import {
  mapMrzToVaultValues,
  mergeModelReadings,
} from "./passportMrz";

/**
 * Reusable on-device MRZ scanner around a single tesseract.js worker. The
 * worker costs seconds to boot (WASM + language model), so the live camera
 * loop must not pay that per frame — create once, `scan` many, `dispose`.
 * Everything it fetches (worker, core, language model) is served from our own
 * origin; no image ever leaves the device.
 */

const MRZ_WHITELIST = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<";
// Photos come off phone cameras at 4000px+; OCR time grows with area and the
// MRZ stays comfortably readable at this width.
const MAX_IMAGE_WIDTH = 2000;
// The MRZ sits in the bottom band of the photo page on every passport.
// Cropping to it reads dramatically cleaner than the full page — the page
// layout confuses segmentation and shrinks the glyphs. The band is rendered
// from the SOURCE pixels to about this width (up- or downscaling as needed),
// so a high-res capture keeps its detail.
const MRZ_BAND_TOP_FRACTION = 0.6;
const MRZ_BAND_TARGET_WIDTH = 2000;

const SCANNABLE_KEYS = [
  "passport_number",
  "date_of_birth",
  "gender",
  "legal_first_name",
  "legal_last_name",
] as const;

export interface MrzScanner {
  /**
   * OCR one image and map trustworthy MRZ fields; null when nothing read.
   * `bandOnly` skips the full-image fallback — right for viewfinder frames,
   * where the framing guide already rules out the unusual framings the
   * fallback exists for, and it halves per-frame OCR cost.
   */
  scan(
    image: Blob | File,
    options?: { bandOnly?: boolean },
  ): Promise<Record<string, string> | null>;
  dispose(): Promise<void>;
}

type TesseractWorker = Awaited<
  ReturnType<(typeof import("tesseract.js"))["createWorker"]>
>;

/**
 * Resampling policy, learned the hard way: a FRACTIONAL nearest-neighbor
 * rescale duplicates/drops pixel columns and hallucinates strokes (a 1.11x
 * band upscale turned ERIKSSON into ERTIKSSON), so near-native crops are not
 * resampled at all, upscales snap to a clean integer with nearest-neighbor
 * (sharp glyph edges), and only genuine downscales use smoothing.
 */
function bandScale(width: number): number {
  const raw = MRZ_BAND_TARGET_WIDTH / width;
  if (raw >= 1.9) return Math.round(raw);
  if (raw > 0.9) return 1;
  return raw;
}

function renderToBlob(
  bitmap: ImageBitmap,
  sourceTop: number,
  scale: number,
): Promise<Blob | null> {
  const sourceHeight = bitmap.height - sourceTop;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(sourceHeight * scale);
  const ctx = canvas.getContext("2d");
  if (ctx === null) return Promise.resolve(null);
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.drawImage(
    bitmap,
    0,
    sourceTop,
    bitmap.width,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
}

/**
 * The attempts to OCR, best first: the MRZ band crop, then the whole image as
 * a fallback for unusual framings (e.g. the passport fills only part of the
 * picture, so the band lands elsewhere).
 */
async function prepareImages(
  image: Blob | File,
  bandOnly: boolean,
): Promise<(Blob | File)[]> {
  const bitmap = await createImageBitmap(image);
  try {
    const band = await renderToBlob(
      bitmap,
      Math.floor(bitmap.height * MRZ_BAND_TOP_FRACTION),
      bandScale(bitmap.width),
    );
    if (bandOnly) return band !== null ? [band] : [image];
    const full =
      bitmap.width <= MAX_IMAGE_WIDTH
        ? image
        : await renderToBlob(bitmap, 0, MAX_IMAGE_WIDTH / bitmap.width);
    return [band, full ?? image].filter(
      (candidate): candidate is Blob | File => candidate !== null,
    );
  } finally {
    bitmap.close();
  }
}

/**
 * Two OCR models read every image and their results merge per-field:
 *
 * - `mrz` (DoubangoTelecom's tesseractMRZ, BSD-3) is trained on OCR-B, the
 *   font passports actually use — measurably better on the NAME line, which
 *   is the one part of the MRZ with no check digit to catch a misread.
 * - `eng` (tessdata_fast) reads the data line more reliably in practice.
 *
 * Check-digit-gated fields take whichever model's reading validates; names
 * prefer the font-trained model. A field neither model validates stays blank
 * — the scanner fails blank, not wrong.
 */
export async function createMrzScanner(): Promise<MrzScanner> {
  // Loaded on demand: the OCR engine is a multi-MB WASM bundle nobody should
  // pay for just to open the form. tesseract.js ships CJS, so depending on
  // the bundler's interop the API surface is the module itself (build) or its
  // `default` (Vite dev).
  const imported: unknown = await import("tesseract.js");
  const { createWorker } = ((imported as { default?: unknown }).default ??
    imported) as typeof import("tesseract.js");
  const boot = async (lang: string): Promise<TesseractWorker> => {
    const worker = await createWorker(lang, 1, {
      workerPath: "/tesseract/worker.min.js",
      corePath: "/tesseract/",
      langPath: "/tesseract",
    });
    await worker.setParameters({
      tessedit_char_whitelist: MRZ_WHITELIST,
    });
    return worker;
  };
  const [engWorker, mrzWorker] = await Promise.all([
    boot("eng"),
    boot("mrz"),
  ]);

  let disposed = false;
  return {
    async scan(
      image: Blob | File,
      options?: { bandOnly?: boolean },
    ): Promise<Record<string, string> | null> {
      // Candidates (band crop, then full image) UNION rather than first-hit:
      // the band usually reads the data line best while the full image can
      // rescue the name line, so stopping at the first partial read would
      // throw the better half away. Earlier candidates win conflicts.
      let combined: Record<string, string> = {};
      for (const candidate of await prepareImages(
        image,
        options?.bandOnly ?? false,
      )) {
        if (disposed) return null;
        const [engResult, mrzResult] = await Promise.all([
          engWorker.recognize(candidate),
          mrzWorker.recognize(candidate),
        ]);
        if (disposed) return null;
        const merged = mergeModelReadings(
          mapMrzToVaultValues(engResult.data.text) ?? {},
          mapMrzToVaultValues(mrzResult.data.text) ?? {},
        );
        combined = { ...merged, ...combined };
        if (SCANNABLE_KEYS.every((key) => key in combined)) break;
      }
      return Object.keys(combined).length > 0 ? combined : null;
    },
    async dispose(): Promise<void> {
      if (!disposed) {
        disposed = true;
        await Promise.all([engWorker.terminate(), mrzWorker.terminate()]);
      }
    },
  };
}
